<template>
  <div>
    <template v-if="verificationToken">
      <p class="text-grey-dark mb-4">
        We are verifying and validating your new email. Please wait a few seconds.
      </p>
    </template>
    <template v-else>
      <p class="text-grey-dark mb-4">
        We have sent you a verification email with a code and a link. Please click the link in the email or enter the code below. If you have not received the email, you can
        <a
          href="#"
          class="ml-0 link-cta"
          @click.prevent="$emit('resend')">
          resend here
        </a>.
      </p>
      
      <app-alert
        color="warning"
        icon-pack="feather"
        icon="icon-alert-triangle"
        :active="!!invalidCodeError"
        type="default">
        <p class="pt-2">
          {{ $t('EnteredCodeInvalidAlertMsg') }}
          <a
            href="#"
            class="ml-0 link-cta"
            @click.prevent="$emit('resend')">
            {{ $t('ResendNewVerificationEmail') | lowercase }}.
          </a>
        </p>
      </app-alert>
      <form @submit.prevent>
        <div class="vx-row mb-6 mt-6">
          <div class="vx-col w-full">
            <vs-input
              v-model="verificationCode"
              class="w-full required"
              :label="$t('VerificationCode')"
              :name="$t('VerificationCode')"
              v-validate="'required|digits:6'"
              data-vv-validate-on="blur|input"
              :danger="verifyCodeInputIsInvalid"
              :danger-text="invalidEnteredCodeErrorMsg"
              val-icon-danger="clear"
              @input="invalidCodeError = ''"/>
          </div>
        </div>

        <base-form-footer-action-buttons
          @save="verifyEmail"
          @cancel="$emit('close')">
          <template v-slot:save>
            {{ $t('$General.Verify')}}
          </template>
        </base-form-footer-action-buttons>
      </form>
    </template>
  </div>
</template>

<script>
// Components
import BaseFormFooterActionButtons from '@/views/modules/_components/BaseFormFooterActionButtons.vue';
import AppAlert from '@/views/_components/AppAlert.vue';

// Services
import { mapActions } from 'vuex';

/**
 * Component to verify a new profile user email
 *
 * @module views/modules/user-account/UserAccountProfileBasicVerifyEmail
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'UserAccountProfileBasicVerifyEmail',
  i18n: {
    messages: {
      en: {
        VerificationCode: 'Verification code',
        VerifyEmailAlertMsg: 'We have sent you a verification email with a code and a link. Please click the link in the email or enter the code below. If you have not received the email, you can',
        VerificationEmailByLinkMsg: 'We are verifying and validating your new email. Please wait a few seconds.',
        EnteredCodeInvalidAlertMsg: 'Email account could not be verified. Make sure the code is correct and valid. If you have not received the email, you can',
        UpdateBasicEmailNotificationTitle: 'Email profile update',
        UpdateBasicEmailNotificationMsg: 'Email profile updated successfully',
        UpdateBasicEmailTokenErrorNotificationTitle: 'Email profile update',
        UpdateBasicEmailTokenErrorNotificationMsg: 'Verification link expired or invalid',
        ResendNewVerificationEmail: 'Resend here',
      },
    },
  },
  components: {
    BaseFormFooterActionButtons,
    AppAlert,
  },
  props: {
    email: {
      type: String,
      required: true,
    },
    verificationToken: {
      type: String,
    },
  },
  data() {
    return {
      verificationCode: '',
      invalidCodeError: '',
    };
  },
  computed: {
    verifyCodeInputIsInvalid() {
      return this.errors.has(this.$t('VerificationCode'))
        || !!this.invalidCodeError;
    },
    invalidEnteredCodeErrorMsg() {
      return this.errors.first(this.$t('VerificationCode')) || this.invalidCodeError;
    },
  },
  created() {
    if (this.verificationToken) {
      this.verifyEmail();
    }
  },
  methods: {
    ...mapActions({
      verifyProfileEmail: 'auth/verifyProfileEmail',
    }),
    async verifyEmail() {
      const formIsValid = await this.$validator.validate();

      if (!formIsValid && !this.verificationToken) {
        document.getElementsByName([this.$validator.errors.items[0].field])[0].focus();
        return;
      }

      this.$vs.loading({ type: 'radius' });

      try {
        await this.verifyProfileEmail({
          email: this.email,
          code: this.verificationCode,
          token: this.verificationToken,
        });

        this.$showSuccessActionNotification({
          title: this.$t('UpdateBasicEmailNotificationTitle'),
          text: this.$t('UpdateBasicEmailNotificationMsg'),
        });

        this.verificationCode = '';
        this.$emit('verified');
      } catch (err) {
        if (this.verificationToken) {
          this.$showGeneralErrorNotification({
            title: this.$t('UpdateBasicEmailTokenErrorNotificationTitle'),
            text: this.$t('UpdateBasicEmailTokenErrorNotificationMsg'),
          });
          this.$emit('close');
        } else if (err.response.data.statusCode === 400 && err.response.data.invalidCode) {
          this.invalidCodeError = err.response.data.message;
        }
      } finally {
        this.$vs.loading.close();
      }
    },
  },
};
</script>
